import { cn } from "../lib/utils";

/**
 * Blendsafe brand kit.
 *
 * — `LogoSymbol`        : the artwork mark only (PNG with transparent BG)
 * — `LogoWordmark`      : just the "Blendsafe" text (3 typography variants)
 * — `LogoHorizontal`    : symbol + wordmark side by side (navbar, share OG)
 * — `LogoCentered`      : symbol stacked above wordmark (splash, footer)
 *
 * All wordmark/lockup components accept a `variant` prop so the same brand
 * can switch between three carefully-tuned typography systems:
 *
 *   • `editorial`  — Instrument Serif, roman + italic accent. The default.
 *                    Aesop / Rhode / NYT-magazine feel. Best for navbar +
 *                    social tiles where elegance carries the brand.
 *   • `clinical`   — Inter ExtraLight uppercase, very wide tracking. The
 *                    minimal, derm-clinic, Apple-product-page feel. Best
 *                    when paired with dense UI (admin, dashboards).
 *   • `luxe`       — Fraunces variable serif with high optical sizing +
 *                    soft tracking. The contemporary beauty-tech feel
 *                    (think Glossier x premium DTC). Best for splash,
 *                    paywall, hero, marketing.
 *
 * The tagline ("Skincare × Medication Safety") is rendered as a separate
 * `LogoLockup` component (see below) — softer weight, muted gray, and
 * generous letter-spacing tuned per typography variant.
 */

// ───────────────────────────────────────────────────────────────────────────
//  utilities
// ───────────────────────────────────────────────────────────────────────────

function isDarkColor(hex) {
  if (!hex || typeof hex !== "string") return false;
  const m = hex.replace("#", "");
  if (m.length !== 3 && m.length !== 6) return false;
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

// Brand-wide default variant. Overridable per-instance.
const DEFAULT_VARIANT = "editorial";

const VARIANTS = ["editorial", "clinical", "luxe"];
const safeVariant = (v) => (VARIANTS.includes(v) ? v : DEFAULT_VARIANT);

// Tagline color — slightly warm muted gray so it sits softly under the
// charcoal wordmark instead of competing with it.
const TAGLINE_COLOR_LIGHT = "#4a4842"; // on cream / off-white
const TAGLINE_COLOR_DARK  = "#d1cdc4"; // on charcoal / dark

// ───────────────────────────────────────────────────────────────────────────
//  Symbol (the wavy mark — image asset, brand-locked)
// ───────────────────────────────────────────────────────────────────────────

export function LogoSymbol({
  size = 36,
  color = "#111111",
  className,
  testId = "logo-symbol",
  // legacy props, ignored
  // eslint-disable-next-line no-unused-vars
  accent,
  // eslint-disable-next-line no-unused-vars
  withAccent,
  // eslint-disable-next-line no-unused-vars
  inner,
}) {
  const src = isDarkColor(color)
    ? "/brand/blendsafe-symbol.png"        // dark stroke for cream BG
    : "/brand/blendsafe-symbol-white.png"; // cream stroke for dark BG
  return (
    <img
      src={src}
      alt="Blendsafe"
      data-testid={testId}
      className={cn("shrink-0 select-none", className)}
      style={{ height: size, width: "auto" }}
      draggable={false}
    />
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Wordmark — 3 typography variants
// ───────────────────────────────────────────────────────────────────────────

/**
 * Wordmark: "Blendsafe".
 *
 * Props:
 *   variant: "editorial" | "clinical" | "luxe"
 *   color:   text color (default charcoal). Pass `#fafafa` for dark BG.
 *   size:    font-size in px (number).
 */
export function LogoWordmark({
  variant = DEFAULT_VARIANT,
  color = "#111111",
  size = 22,
  className,
  testId,
}) {
  const v = safeVariant(variant);

  if (v === "clinical") {
    // Ultra-light Inter, uppercase, wide tracking — derm/clinical minimal.
    return (
      <span
        data-testid={testId || `logo-wordmark-${v}`}
        className={cn(
          "font-sans whitespace-nowrap select-none",
          className,
        )}
        style={{
          color,
          fontSize: size,
          fontWeight: 300,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        {/* The trailing letter-spacing pushes the visual block to the right;
            compensate with a negative margin so the optical alignment with
            the symbol stays balanced. */}
        <span style={{ marginRight: "-0.22em" }}>Blendsafe</span>
      </span>
    );
  }

  if (v === "luxe") {
    // Fraunces variable serif — soft optical sizing, medium weight, almost
    // no tracking. Contemporary luxury beauty-tech.
    return (
      <span
        data-testid={testId || `logo-wordmark-${v}`}
        className={cn(
          "font-luxe whitespace-nowrap select-none",
          className,
        )}
        style={{
          color,
          fontSize: size,
          fontWeight: 450,
          letterSpacing: "-0.012em",
          // Fraunces variable axes — soft optical sizing for "skincare" feel.
          fontVariationSettings: '"opsz" 96, "SOFT" 50',
          lineHeight: 1,
        }}
      >
        Blendsafe
      </span>
    );
  }

  // editorial (default) — Apothecary two-tone serif: tall, condensed
  // Cormorant Garamond letterforms at bold weight with capitalized B + S
  // and a two-tone green color story: deep forest "Blend" + sage "Safe".
  // Inspired by botanical-pharmacy / clean beauty packaging.
  //
  // When the caller passes a non-default `color` (e.g. cream on dark BG), we
  // keep both halves the same color so the wordmark still reads on a dark
  // surface; otherwise we use the two-tone palette.
  const isDefaultColor = !color || color === "#111111";
  const blendColor = isDefaultColor ? "#1f3d2e" : color; // deep forest
  const safeColor = isDefaultColor ? "#7a9484" : color;  // sage
  return (
    <span
      data-testid={testId || `logo-wordmark-${v}`}
      className={cn("whitespace-nowrap select-none", className)}
      style={{
        fontFamily: '"Cormorant Garamond", "Italiana", ui-serif, Georgia, serif',
        fontSize: size,
        letterSpacing: "0.005em",
        lineHeight: 1,
      }}
    >
      <span style={{ color: blendColor, fontWeight: 700 }}>Blend</span>
      <span style={{ color: safeColor, fontWeight: 700, marginLeft: "0.06em" }}>Safe</span>
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Tagline — softer, smaller, muted gray
// ───────────────────────────────────────────────────────────────────────────

export function LogoTagline({
  variant = DEFAULT_VARIANT,
  color,
  size = 11,
  className,
}) {
  const v = safeVariant(variant);

  // Per-variant tagline typography — keeps the brand voice consistent
  // within each system but doesn't repeat the wordmark's character.
  // Weights are intentionally bumped slightly higher than typical taglines
  // because this string ("Skincare × Medication Safety") IS the product
  // description — it has to read clearly on first glance.
  const styles = {
    editorial: {
      // Roman Cormorant Garamond — quiet serif counterpoint to the tall
      // condensed Italiana wordmark. Generous letter-spacing for breathing
      // room; tracking the wordmark's stately rhythm without competing.
      fontFamily: '"Cormorant Garamond", ui-serif, Georgia, serif',
      fontWeight: 600,
      fontStyle: "italic",
      letterSpacing: "0.04em",
    },
    clinical: {
      // Match the wordmark's uppercase tracking but stay smaller + lighter.
      fontFamily: '"Inter", system-ui, sans-serif',
      fontWeight: 600,
      letterSpacing: "0.24em",
      textTransform: "uppercase",
    },
    luxe: {
      // Roman Inter, medium weight, slight positive tracking.
      fontFamily: '"Inter", system-ui, sans-serif',
      fontWeight: 500,
      letterSpacing: "0.03em",
    },
  };

  // Tagline copy — the "×" is colored to match the orange heart in the icon
  // so the whole brand system shares one accent color. The × is rendered
  // slightly larger and non-italic so it reads as an icon, not a letter.
  const ACCENT_ORANGE = "#e8693a";
  const accentX = (
    <span
      style={{
        color: ACCENT_ORANGE,
        fontStyle: "normal",
        fontWeight: 700,
        fontSize: "1.15em",
        margin: "0 0.18em",
        verticalAlign: "baseline",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      ×
    </span>
  );
  const taglineText = (
    <>
      Skincare{accentX}Medication Safety
    </>
  );

  return (
    <span
      className={cn("whitespace-nowrap select-none", className)}
      style={{
        color: color || TAGLINE_COLOR_LIGHT,
        fontSize: size,
        lineHeight: 1.1,
        ...styles[v],
      }}
    >
      {taglineText}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Lockups
// ───────────────────────────────────────────────────────────────────────────

/** Horizontal lockup — symbol + (wordmark / wordmark+tagline) inline. */
export function LogoHorizontal({
  variant = DEFAULT_VARIANT,
  color = "#111111",
  size = 36,
  textSize = 22,
  taglineSize = 11,
  withTagline = false,
  gap = 14,
  className,
  testId,
  // legacy
  // eslint-disable-next-line no-unused-vars
  accent,
  // eslint-disable-next-line no-unused-vars
  withAccent,
}) {
  const v = safeVariant(variant);
  const taglineColor = isDarkColor(color) ? TAGLINE_COLOR_DARK : TAGLINE_COLOR_LIGHT;

  return (
    <span
      data-testid={testId || "logo-horizontal"}
      className={cn("inline-flex items-center select-none", className)}
      style={{ gap: `${gap}px` }}
    >
      <LogoSymbol size={size} color={color} />
      {withTagline ? (
        <span className="inline-flex flex-col" style={{ gap: 4 }}>
          <LogoWordmark variant={v} color={color} size={textSize} />
          <LogoTagline variant={v} color={taglineColor} size={taglineSize} />
        </span>
      ) : (
        <LogoWordmark variant={v} color={color} size={textSize} />
      )}
    </span>
  );
}

/** Centered lockup — symbol stacked above wordmark, used for splash / footer. */
export function LogoCentered({
  variant = DEFAULT_VARIANT,
  color = "#111111",
  size = 56,
  textSize = 26,
  taglineSize = 12,
  withTagline = true,
  className,
  testId,
  // legacy
  // eslint-disable-next-line no-unused-vars
  accent,
  // eslint-disable-next-line no-unused-vars
  withAccent,
}) {
  const v = safeVariant(variant);
  const taglineColor = isDarkColor(color) ? TAGLINE_COLOR_DARK : TAGLINE_COLOR_LIGHT;
  return (
    <span
      data-testid={testId || "logo-centered"}
      className={cn("inline-flex flex-col items-center", className)}
      style={{ gap: 18 }}
    >
      <LogoSymbol size={size} color={color} />
      <span className="inline-flex flex-col items-center" style={{ gap: 8 }}>
        <LogoWordmark variant={v} color={color} size={textSize} />
        {withTagline && (
          <LogoTagline variant={v} color={taglineColor} size={taglineSize} />
        )}
      </span>
    </span>
  );
}
